import { useCallback, useMemo } from 'react';

import { usePreferencesStore } from '~/store/preferences.store';

/** The two fields this hook needs from a table's column registry. */
export interface ColumnVisibilityDefault {
  key: string;
  defaultVisible: boolean;
}

/**
 * Column visibility for one table, remembered across sessions.
 *
 * The stored value is sparse — only columns the user has actually toggled — and
 * it is merged over the table's current defaults on every read. That is what
 * keeps it safe as a table evolves: a column added later shows up at its own
 * default rather than being stuck hidden by an older payload, and a column
 * removed later leaves nothing behind to resurrect.
 *
 * Lives in the persisted preferences store rather than touching localStorage
 * directly, per the project's storage rule.
 *
 * @param tableId Stable id for the table. Changing it forgets the preference.
 * @param columns The table's column registry — must be referentially stable
 *   (a module-level constant), since it keys the merge memo.
 */
export function useTableColumnVisibility(
  tableId: string,
  columns: readonly ColumnVisibilityDefault[],
): [Record<string, boolean>, (columnId: string, visible: boolean) => void] {
  const stored = usePreferencesStore((s) => s.tableColumns[tableId]);
  const setTableColumn = usePreferencesStore((s) => s.setTableColumn);

  const visibility = useMemo(() => {
    const merged: Record<string, boolean> = {};
    for (const column of columns) {
      merged[column.key] = stored?.[column.key] ?? column.defaultVisible;
    }
    return merged;
  }, [columns, stored]);

  const setColumnVisible = useCallback(
    (columnId: string, visible: boolean) => {
      setTableColumn(tableId, columnId, visible);
    },
    [setTableColumn, tableId],
  );

  return [visibility, setColumnVisible];
}
